/**
 * ACCOUNT-SCOPED, EXPIRING localStorage FOR SLOW-CHANGING ANSWERS.
 *
 * Two things on the CRM page are fetched before anything can render its own chrome: who is
 * in the caller's agency (`useOrgDirectory`) and what the caller may do there
 * (`useCrmMyAccess`). Both are module-cached, which is enough while the SPA stays mounted
 * and worth nothing across a reload — and the second cannot even START until the first
 * answers, because it needs the org id. So a hard reload costs TWO sequential round trips
 * before the tab bar knows whether it exists, during which the page shows a hole where the
 * tabs go. Reported as "it still takes too much time to load the tabs, and until they are
 * loaded it shows blank and it's weird".
 *
 * Neither answer changes often — an agency roster changes when support edits it, a
 * permission when an admin does — so the previous answer is almost always the current one.
 * Persisting it turns both requests into background revalidation: the page paints from what
 * it knew, and corrects itself if anything moved.
 *
 * ── What this is NOT ────────────────────────────────────────────────────────────
 *
 * ⚠️ It is not an authorization input, and nothing may treat it as one. Everything stored
 * through here is a LABEL or a LAYOUT hint: a colleague's name, an agency's name, whether a
 * tab is enabled. Scope is resolved server-side from `dashboard-orgs` on every single
 * request, so the worst a hand-edited value can do is render a tab that answers 403 when
 * used, or a name nobody has.
 *
 * ── The three rules that make it safe ───────────────────────────────────────────
 *
 *  1. STAMPED WITH THE ACCOUNT. A cache written for one email is never returned for
 *     another, so signing in as somebody else cannot inherit their colleagues. The sign-out
 *     path clears it outright rather than relying on that.
 *  2. EXPIRES. A browser that has not opened the app for a week gets nothing back — a
 *     roster that old is more likely to mislead than to help, and the request that replaces
 *     it is one round trip.
 *  3. NEVER PERSIST A FAILURE. Both callers cache "no agency" in memory when a lookup
 *     fails, deliberately (the narrow answer is the safe one). Persisting that would turn
 *     one transient 500 into a feature that is missing for the whole of the next session.
 *
 * Every access is wrapped: `localStorage` throws outright in a Safari private window and
 * when a browser is set to block site data, and a cache miss must never be an exception.
 */

type Envelope<T> = { email: string; at: number; value: T };

/** Older than this and it is not worth showing. See rule 2. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const normalizeEmail = (value?: string | null): string => (value ?? "").trim().toLowerCase();

export function readPersistedCache<T>(key: string, email?: string | null): { value: T; at: number } | null {
  const owner = normalizeEmail(email);
  if (!owner) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Envelope<T> | null;
    if (!parsed || parsed.email !== owner || typeof parsed.at !== "number") return null;
    if (Date.now() - parsed.at > MAX_AGE_MS) return null;
    return { value: parsed.value, at: parsed.at };
  } catch {
    // Unreadable, unparseable, or storage disabled — all of them mean "no cache", which is
    // a state every caller already handles.
    return null;
  }
}

export function writePersistedCache<T>(key: string, email: string | null | undefined, value: T): void {
  const owner = normalizeEmail(email);
  if (!owner) return;
  try {
    const envelope: Envelope<T> = { email: owner, at: Date.now(), value };
    window.localStorage.setItem(key, JSON.stringify(envelope));
  } catch {
    // Quota, private mode, blocked storage. A cache that cannot be written is a cache that
    // will not be read; nothing else changes.
  }
}

export function clearPersistedCache(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // As above.
  }
}
